/**
 * The live rep count (todo 8) on a running motion source: polls the new samples a few times per second and feeds
 * them to the counter of `src/core/reps.ts` from the start of the set on (the lead-in, stowing the phone, does not
 * count). Used by the sensor sets of a workout (`useRepSensor`) and the Zähltest (`RecordScreen`).
 */
import { countReps, repCounter, type RepProfile } from "../core/reps";
import type { Columns, RawRecording } from "../core/motion";
import type { MotionSource } from "./motionSource";

const POLL_MS = 250;

export interface LiveCount {
  /** the set starts at `at` (ms since 1970); samples before it are not counted */
  begin(at: number): void;
  readonly count: number;
  /** false when the source hands over no samples while recording (an older APK): the count comes at the end */
  readonly live: boolean;
  /**
   * The final count from the stopped source's recording, over its set window (the last setStart and setEnd marks;
   * without them from `begin` to the end). Stops polling.
   */
  finish(raw: RawRecording): number;
  /** stops polling */
  dispose(): void;
}

/**
 * Starts counting on a source that was started at `startedAt` (ms since 1970, the recording's clock 0).
 * `onCount` is called whenever the count changes.
 */
export function liveCount(src: MotionSource, startedAt: number, profile: RepProfile | undefined, onCount: (n: number) => void): LiveCount {
  const counter = repCounter(profile);
  const seen: Columns = { t: [], x: [], y: [], z: [] };
  let fed = 0;
  /** the set start on the recording's clock */
  let from: number | undefined;
  let live = true;
  let busy = false;
  let stopped = false;
  let reported = 0;

  const feed = () => {
    if (from === undefined) return;
    for (; fed < seen.t.length; fed++) {
      if (seen.t[fed] >= from) counter.push(seen.t[fed], seen.x[fed], seen.y[fed], seen.z[fed]);
    }
    if (counter.count !== reported) {
      reported = counter.count;
      onCount(reported);
    }
  };

  const poll = async () => {
    if (busy || !live || stopped) return;
    busy = true;
    try {
      const c = await src.read(seen.t.length);
      if (stopped) return;
      seen.t.push(...c.t); seen.x.push(...c.x); seen.y.push(...c.y); seen.z.push(...c.z);
      feed();
    } catch {
      // an APK older than 0.3.6: no samples until the end
      live = false;
    } finally {
      busy = false;
    }
  };
  const timer = setInterval(() => void poll(), POLL_MS);

  return {
    begin(at) {
      if (from !== undefined) return;
      from = at - startedAt;
      feed();
    },
    get count() { return counter.count; },
    get live() { return live; },
    finish(raw) {
      stopped = true;
      clearInterval(timer);
      const mark = (kind: "setStart" | "setEnd") => raw.marks.filter((m) => m.kind === kind).at(-1)?.t;
      const start = mark("setStart") ?? from ?? 0;
      return countReps(raw.accel, start, mark("setEnd") ?? Infinity, profile).count;
    },
    dispose() {
      stopped = true;
      clearInterval(timer);
    },
  };
}
